import { sendMsg } from '@/lib/api/postMessageAPI';
import { mobx } from '@/lib/mobx';
import { aggregateSocketToStoreBunch } from '@/helpers/aggregateSocketToStoreBunch';
import { getLanguageFromLocation } from '@/helpers/getLanguageFromLocation';
import { socketHost } from '@/config';
import { entityNames, liveLines, rpcTypes } from '@/config/constants';

import { logger } from './logger';
import { MicroQueue, microtaskScheduler } from './MicroQueue';
import { SportsbookManagerEvents, SportsbookSocketManager } from './SportsbookSocketManager';

const language = getLanguageFromLocation();

function processError({ message = '', error = {}, reconnectionCount = 0, type = 'error' }) {
  // Hide preloader in LayoutGames.jsx if socket has error or close.
  mobx.ui.setSocketConfigIsLoaded();

  if (reconnectionCount === 0) {
    logger[type](message, {
      error,
    });
  } else {
    console.error(message, error);
  }

  sendMsg('socket', {
    reason: message,
    status: 'error',
  });
}

class SocketService {
  /**
   * @private
   * @type {Promise<WebSocket> | null}
   */
  connectionPromise = null;

  socketAlert = { hide: () => {} };

  sportsbookSocketManager = null;

  subscriptionId = null;

  microQueue = null;

  constructor() {
    if (!SocketService.instance) {
      SocketService.instance = this;
    }

    this.language = language;
    this.reconnectionCount = 0;

    // biome-ignore lint/correctness/noConstructorReturn: todo fix this
    return SocketService.instance;
  }

  /**
   * @param {object} p
   * @param {string[]=} p.fullLine
   * @param {string=} p.language
   * @param {string=} p.defaultLanguage
   * @param {string=} p.socketHost
   * @param {string[]=} p.filter
   */
  setConfig(p) {
    Object.assign(this, p);
  }

  /**
   * @private
   *
   * @returns {Promise<WebSocket>}
   */
  async connect() {
    this.microQueue = new MicroQueue(
      (batch) => mobx.store.addBunch(batch),
      {
        aggregate: aggregateSocketToStoreBunch,
      },
      microtaskScheduler,
    );

    this.sportsbookSocketManager = SportsbookSocketManager.getInstance();

    if (!this.subscriptionId) {
      const { subscribeId } = this.sportsbookSocketManager.registerSubscriber({
        key: 'RootSocket',
        onConfigChange: this.onConfigUpdate,
      });

      this.subscriptionId = subscribeId;
    }

    this.initListeners();

    await this.sportsbookSocketManager.initialize({
      initParamsGetter: async () => {
        const token = await mobx.profile.getToken();
        const { brand: cid } = mobx.auth;
        const tokenExpiresAt = mobx.profile.user.tokenExpiresAt;
        const tokenReceivedAt = mobx.profile.user.tokenReceivedAt;
        const expired = mobx.profile.isTokenExpired();

        return {
          cid,
          expired,
          language: this.language,
          socketHost,
          token,
          tokenExpiresAt,
          tokenReceivedAt,
        };
      },
      reconnectLimit: 16,
    });

    // TODO: refactor search to remove from here and use getRpc
    this.sportsbookSocketManager.socketEmitter.on('rpc', this.onRpcUpdate);
  }
  initListeners() {
    this.sportsbookSocketManager.on(SportsbookManagerEvents.COUNT, this.onCountUpdate);
    this.sportsbookSocketManager.on(SportsbookManagerEvents.EVENT, this.processEnityUpdate);
    this.sportsbookSocketManager.on(SportsbookManagerEvents.LINE, this.processEnityUpdate);
    this.sportsbookSocketManager.on(SportsbookManagerEvents.DISCIPLINE, this.processEnityUpdate);
    this.sportsbookSocketManager.on(SportsbookManagerEvents.TOURNAMENT, this.processEnityUpdate);
    this.sportsbookSocketManager.on(SportsbookManagerEvents.MARKET, this.processEnityUpdate);
    this.sportsbookSocketManager.on(SportsbookManagerEvents.SCOREBOARD, this.processEnityUpdate);
    this.sportsbookSocketManager.on(SportsbookManagerEvents.WIDGET, this.onWidgetUpdate);

    this.sportsbookSocketManager.on(SportsbookManagerEvents.SEND_ERROR, this.onSendError);

    this.sportsbookSocketManager.on(SportsbookManagerEvents.UNKNOWN_EVENT, this.onUnknownEvent);
    this.sportsbookSocketManager.on(SportsbookManagerEvents.SOCKET_AUTH_ERROR, this.onAuthError);

    this.sportsbookSocketManager.on(SportsbookManagerEvents.RECONNECT, this.onReconnectAttempt);

    this.sportsbookSocketManager.on(
      SportsbookManagerEvents.RECONNECT_SUCCESS,
      this.onReconnectSuccess,
    );
    this.sportsbookSocketManager.on(SportsbookManagerEvents.SOCKET_CLOSED, this.onSocketClosed);
  }

  onReconnectSuccess = () => {
    this.socketAlert.hide();
  };

  onCountUpdate = (payload) => {
    mobx.store.setCounts(payload.data.value);
  };

  onWidgetUpdate = (payload) => {
    this.microQueue.push(payload.data);
  };

  onConfigUpdate = (data) => {
    const { disciplines } = data;

    mobx.ui.setSettings(disciplines);
  };

  processEnityUpdate = (payload) => {
    switch (payload.type) {
      case 'batch': {
        this.processRegularBatchEntity(payload);
        break;
      }
      case 'leave': {
        this.onLeave(payload);
        break;
      }
      case 'error': {
        this.onError(payload);
        break;
      }
      default: {
        break;
      }
    }
  };

  processRegularBatchEntity(payload) {
    const {
      data: {
        action,
        model: entityName,
        value: { lid: id },
      },
    } = payload;

    const storeName = entityNames[entityName];
    if (!storeName) {
      processError({
        error: payload,
        message: 'Unknown socket entity',
        type: 'warn',
      });
      return;
    }

    if (!action) {
      return;
    }

    this.microQueue.push({ ...payload.data });
  }

  onLeave({ data: { model, value, action } }) {
    for (const lid of value) {
      this.microQueue.push({
        action,
        model,
        value: { lid },
      });
    }
  }

  onRpcUpdate = (rpcData) => {
    const { store: mainStore } = mobx;

    const { payload, data } = rpcData;

    if (payload === 'search') {
      const { events, tournaments } = data.results;
      const unsubscribedEventsTournaments = events.reduce(
        (acc, { item }) => (mainStore.events.has(item.lid) ? acc : [...acc, item.pid]),
        [],
      );

      const unsubscribedTournaments = tournaments.reduce(
        (acc, { item }) => (mainStore.tournaments.has(item.lid) ? acc : [...acc, item.lid]),
        [],
      );

      const toSubscribeIds = [
        ...new Set([...unsubscribedTournaments, ...unsubscribedEventsTournaments]),
      ];

      mobx.search.setSearchResponse(data.results);

      return;
    }

    if (!rpcTypes.some((type) => type === payload)) {
      processError({
        error: rpcData,
        message: 'Unknown socket RPC respond',
        type: 'warn',
      });
    }
  };

  onError(errorData) {
    if (!errorData?.data) {
      return;
    }

    const { store: mainStore } = mobx;

    const {
      model,
      value: { id: lid, code, message },
    } = errorData.data;

    const entityMap = mainStore[entityNames[model]];

    if (entityMap) {
      if (mainStore.activeEventSelected && model === 'event') {
        const [line] = lid.split('.');
        const activeEventId = mainStore.activeEventLids[lid];

        if (activeEventId && liveLines.has(line)) {
          const [activeEventLine] = mainStore.activeEventData.lid?.split('.') || [];

          const isEventInCurrentLine = activeEventLine === line;

          if (isEventInCurrentLine) {
            mainStore.setActiveEventIsOver(true);
            return;
          }
        }
      }

      entityMap.delete(lid);
    }

    // Uncomment this if you need it
    // console.warn(message, `code: ${code}`);
  }

  onUnknownEvent = (data) => {
    processError({
      error: data,
      message: 'Unknown socket response',
      type: 'warn',
    });
  };

  onSocketClosed = async ({ wasClean, code, reason }) => {
    mobx.ui.setSocketConfigIsLoaded();
  };

  onSendError = ({ model, payload }) => {
    processError({
      error: payload,
      message: `Socket send error for model: ${model}`,
    });
  };

  onAuthError = ({ payload }) => {
    mobx.profile.markTokenExpired();

    processError({
      error: payload,
      message: `Socket connection error: ${payload.message} (${payload.code})`,
    });
  };

  unsubscribe(model, data) {
    const idsToUnsubscribe = data.filter(Boolean);

    if (idsToUnsubscribe.length) {
      this.sportsbookSocketManager.unsubscribe(this.subscriptionId, model, idsToUnsubscribe);
    }
  }

  /**
   * @param {boolean} resubscribe
   * @param {boolean} isClearStore
   *
   * @returns {Promise<WebSocket | null>}
   */
  async initSocket(resubscribe = false) {
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    if (this.sportsbookSocketManager?.socketEmitter?.getIsWSReady()) {
      return this.sportsbookSocketManager.socketEmitter.ws;
    }

    this.connectionPromise = Promise.resolve().then(async () => {
      await this.connect();

      if (resubscribe) {
        this.sportsbookSocketManager.resubscribe();
      }

      sendMsg('socket', {
        status: 'ok',
      });
      this.socketAlert.hide();
    });

    await this.connectionPromise;

    this.connectionPromise = null;
    return this.sportsbookSocketManager.socketEmitter.ws;
  }

  async subscribe(model, ids) {
    this.sportsbookSocketManager.subscribe(this.subscriptionId, model, ids);
  }

  send = async (type, model, data) => {
    try {
      if (this.sportsbookSocketManager.socketEmitter?.getIsWSReady()) {
        await this.sportsbookSocketManager.socketEmitter.send(type, model, data);
      }
    } catch (error) {
      processError({
        error,
        message: 'Socket send error',
      });
    }
  };

  closeSocket() {
    this.sportsbookSocketManager.disconnectSocket();
  }

  async closeSocketAndReInit(props) {
    const { isClearStore = false } = props || {};
    this.closeSocket();
    await this.initSocket(true, isClearStore);
  }

  onReconnectAttempt = async ({ count, nextAttemptIn }) => {
    let warningText = `Socket reconnect after ${nextAttemptIn}s`;
    let isShowAlertWithConfirm = false;

    if (count >= 15) {
      isShowAlertWithConfirm = true;
    } else if (count >= 5) {
      warningText = `Await socket connection ${nextAttemptIn}s`;
    }

    if (!isShowAlertWithConfirm) {
      this.socketAlert = mobx.ui.setAlert(
        {
          hideBtnClose: true,
          progress: true,
          text: 'SOCKET.ERROR.RECONNECT.PROGRESS',
          timeout: 106000,
          title: 'SOCKET.ERROR.RECONNECT.TITLE',
          type: 'error _text-direction-column',
        },
        true,
      );
      console.warn(warningText);
    } else {
      if (count === 16) {
        this.socketAlert.hide();
      }

      if (!this.socketAlert.retryClicked) {
        const alertAboutBlocking = mobx.ui.getAlert({
          text: 'SOCKET.ERROR.RECONNECT.DESCRIPTION',
          title: 'Failure.',
          type: 'error _text-direction-column',
        });

        if (alertAboutBlocking?.title === 'Failure.') {
          return;
        }

        this.socketAlert = mobx.ui.setAlert({
          button: {
            onClick: () => {
              this.socketAlert.retryClicked = true;
              this.closeSocketAndReInit();
            },
            text: 'SOCKET.ERROR.RECONNECT_BTN',
          },
          hideBtnClose: true,
          text: 'SOCKET.ERROR.DESCRIPTION',
          timeout: false,
          title: 'SOCKET.ERROR.TITLE',
          type: 'error _text-direction-column',
        });
      } else {
        this.socketAlert = mobx.ui.setAlert({
          callback: () => {
            this.socketAlert.retryClicked = false;
            this.closeSocketAndReInit();
          },
          hideBtnClose: true,
          progress: true,
          text: 'SOCKET.ERROR.RECONNECT_DESCRIPTION',
          timeout: 30000,
          title: 'Failure.',
          type: 'error _text-direction-column',
        });
      }
    }
  };
}

export const Socket = new SocketService();
